import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Zap,
  Lock,
  BarChart3,
  Globe,
  Upload,
  Link as LinkIcon,
  CheckCircle2,
  ArrowRight,
  ChevronDown,
  Sparkles,
  Shield,
  TrendingUp,
  Target,
  Settings,
  Code2,
  X,
  XCircle,
  Newspaper,
  Loader2,
  Eye,
  Search,
  Activity,
  ClipboardCheck,
} from 'lucide-react';
import { noticeApi, NoticeListItem, BannerNotice, NoticeDetail } from '@/api/noticeApi';
import NoticeBanner from '@/components/notice/NoticeBanner';

export function LandingPage() {
  const navigate = useNavigate();
  const location = useLocation();

  // 다른 페이지에서 섹션 링크로 이동 시 해당 섹션으로 스크롤
  useEffect(() => {
    const scrollTo = (location.state as { scrollTo?: string } | null)?.scrollTo;
    if (!scrollTo) return;
    const timer = setTimeout(() => {
      const element = document.getElementById(scrollTo);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [location.state]);

  const [notices, setNotices] = useState<NoticeListItem[]>([]);
  const [noticeLoading, setNoticeLoading] = useState(true);
  const [noticeLayerOpen, setNoticeLayerOpen] = useState(false);
  const [selectedNoticeId, setSelectedNoticeId] = useState<number | null>(null);
  const [selectedNotice, setSelectedNotice] = useState<NoticeDetail | null>(null);
  const [selectedNoticeLoading, setSelectedNoticeLoading] = useState(false);
  const [selectedNoticeError, setSelectedNoticeError] = useState<string | null>(null);

  const parseNoticeItems = (payload: unknown): NoticeListItem[] => {
    if (Array.isArray(payload)) return payload as NoticeListItem[];
    if (payload && typeof payload === 'object') {
      const content = (payload as { content?: unknown }).content;
      if (Array.isArray(content)) return content as NoticeListItem[];
    }
    return [];
  };

  useEffect(() => {
    let mounted = true;

    const loadNotices = async () => {
      try {
        // 모든 페이지에서 공지사항을 로드 (페이징 처리)
        const res = await noticeApi.getList({ page: 0, size: 100 });
        const list = parseNoticeItems(res.data);
        
        if (!mounted) return;

        if (list.length > 0) {
          setNotices(list);
        } else {
          // 공지사항이 없으면 배너 공지만 로드
          const bannerRes = await noticeApi.getBanner();
          if (!mounted) return;
          const banners = Array.isArray(bannerRes.data) ? bannerRes.data : [];
          const fallback: NoticeListItem[] = banners.slice(0, 50).map((b: BannerNotice) => ({
            id: b.id,
            title: b.title,
            isPinned: false,
            isBanner: true,
            viewCount: 0,
            createdAt: b.createdAt,
          }));
          setNotices(fallback);
        }
      } catch (error) {
        // 에러 시에도 배너 공지는 시도하기
        try {
          const bannerRes = await noticeApi.getBanner();
          if (!mounted) return;
          const banners = Array.isArray(bannerRes.data) ? bannerRes.data : [];
          const fallback: NoticeListItem[] = banners.slice(0, 5).map((b: BannerNotice) => ({
            id: b.id,
            title: b.title,
            isPinned: false,
            isBanner: true,
            viewCount: 0,
            createdAt: b.createdAt,
          }));
          setNotices(fallback);
        } catch {
          if (mounted) {
            setNotices([]);
          }
        }
      } finally {
        if (mounted) setNoticeLoading(false);
      }
    };

    loadNotices();
    return () => {
      mounted = false;
    };
  }, []);

  const sortedNotices = useMemo(
    () => [...notices].sort((a, b) => {
      if (a.isBanner !== b.isBanner) return Number(b.isBanner) - Number(a.isBanner);
      if (a.isPinned !== b.isPinned) return Number(b.isPinned) - Number(a.isPinned);
      return b.createdAt.localeCompare(a.createdAt);
    }),
    [notices]
  );

  const headlineNotice = sortedNotices[0] ?? null;
  const showNoticeUi = !noticeLoading && sortedNotices.length > 0;

  const formatNoticeDate = (iso: string) =>
    iso ? new Date(iso).toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' }) : '';

  const openNoticeLayer = (noticeId?: number) => {
    if (noticeId) {
      setSelectedNoticeId(noticeId);
    }
    setNoticeLayerOpen(true);
  };

  useEffect(() => {
    if (!noticeLayerOpen || sortedNotices.length === 0) return;
    setSelectedNoticeId((prev) => {
      if (prev && sortedNotices.some((notice) => notice.id === prev)) {
        return prev;
      }
      return sortedNotices[0].id;
    });
  }, [noticeLayerOpen, sortedNotices]);

  useEffect(() => {
    if (!noticeLayerOpen || !selectedNoticeId) return;

    let mounted = true;
    setSelectedNoticeLoading(true);
    setSelectedNoticeError(null);
    setSelectedNotice(null);

    noticeApi.getDetail(selectedNoticeId)
      .then((res) => {
        if (!mounted) return;
        setSelectedNotice(res.data);
      })
      .catch(() => {
        if (!mounted) return;
        setSelectedNoticeError('공지사항 내용을 불러오는데 실패했습니다.');
      })
      .finally(() => {
        if (mounted) setSelectedNoticeLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [noticeLayerOpen, selectedNoticeId]);

  return (
    <>
      <Helmet>
        <title>ALT Generation - AI 이미지 대체텍스트 자동 생성</title>
        <meta name="description" content="AI로 이미지 alt 텍스트를 자동 생성하세요. 웹 접근성 준수와 SEO를 동시에 해결하는 이미지 대체텍스트 자동화 서비스." />
        <link rel="canonical" href="https://alt.aigeneration-tech.com/" />
        <meta property="og:title" content="ALT Generation - AI 이미지 대체텍스트 자동 생성" />
        <meta property="og:description" content="AI로 이미지 alt 텍스트를 자동 생성하세요. 웹 접근성 준수와 SEO를 동시에 해결하는 이미지 대체텍스트 자동화 서비스." />
        <meta property="og:url" content="https://alt.aigeneration-tech.com/" />
        <meta property="og:type" content="website" />
      </Helmet>
      <div className="min-h-screen flex flex-col">
        <Header />
      
      <main className="flex-1">
        <NoticeBanner />

        {showNoticeUi && headlineNotice && (
          <section className="bg-slate-50 border-b border-slate-200">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="max-w-6xl mx-auto flex items-center gap-3 py-2.5">
                <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap">
                  <Newspaper className="w-3.5 h-3.5" /> 공지
                </span>

                <button
                  onClick={() => openNoticeLayer(headlineNotice.id)}
                  className="flex-1 text-left text-sm text-gray-700 hover:text-primary transition-colors truncate"
                >
                  {headlineNotice.title}
                </button>

                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 px-3 text-xs"
                  onClick={() => openNoticeLayer(headlineNotice.id)}
                >
                  전체 공지 보기
                </Button>
              </div>
            </div>
          </section>
        )}

        {showNoticeUi && (
          <Dialog open={noticeLayerOpen} onOpenChange={setNoticeLayerOpen}>
            <DialogContent className="max-w-4xl p-0">
              <DialogHeader className="px-6 pt-6 pb-3 border-b">
                <DialogTitle className="flex items-center gap-2">
                  <Newspaper className="w-5 h-5 text-primary" />
                  전체 공지
                </DialogTitle>
                <DialogDescription>
                  공지 제목을 선택하면 레이어에서 본문 전체를 바로 확인할 수 있습니다.
                </DialogDescription>
              </DialogHeader>

              <div className="max-h-[75vh] overflow-y-auto">
                <div className="px-6 py-4 border-b bg-gray-50">
                  <div className="flex flex-wrap gap-2">
                    {sortedNotices.map((notice) => (
                      <button
                        key={notice.id}
                        className={`px-3 py-2 rounded-md border text-left transition-colors ${selectedNoticeId === notice.id ? 'border-primary bg-primary/5' : 'border-gray-200 bg-white hover:bg-gray-100'}`}
                        onClick={() => setSelectedNoticeId(notice.id)}
                      >
                        <p className="text-sm text-gray-800 font-medium max-w-[18rem] truncate">{notice.title}</p>
                        <p className="text-xs text-gray-400 mt-1">{formatNoticeDate(notice.createdAt)}</p>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="px-6 py-5">
                  {selectedNoticeLoading ? (
                    <div className="flex justify-center py-10 text-gray-500 text-sm">
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      공지사항 본문을 불러오는 중입니다.
                    </div>
                  ) : selectedNoticeError ? (
                    <p className="text-center text-red-500 py-10">{selectedNoticeError}</p>
                  ) : selectedNotice ? (
                    <article className="space-y-4">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs text-gray-400">{formatNoticeDate(selectedNotice.createdAt)}</span>
                        <span className="text-xs text-gray-400 inline-flex items-center gap-1">
                          <Eye className="w-3.5 h-3.5" />
                          {(selectedNotice.viewCount ?? 0).toLocaleString()}
                        </span>
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 break-words">{selectedNotice.title}</h3>
                      <div
                        className="notice-html text-gray-800"
                        style={{ minHeight: '220px' }}
                        dangerouslySetInnerHTML={{ __html: selectedNotice.content }}
                      />
                    </article>
                  ) : (
                    <p className="text-center text-gray-400 py-10">선택된 공지사항이 없습니다.</p>
                  )}
                </div>
              </div>

              <div className="px-6 py-4 border-t flex justify-end bg-gray-50">
                <Button size="sm" variant="outline" onClick={() => setNoticeLayerOpen(false)}>
                  닫기
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Hero Section */}
        <section className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-20 md:py-32">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="inline-flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-full mb-4"
              >
                <Sparkles className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium text-primary">AI 기반 접근성 개선 어시스턴트</span>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="text-4xl md:text-6xl font-bold text-gray-900 mb-3"
              >
                이미지 ALT 텍스트를<br />
                <span className="text-primary">자동으로 생성하세요</span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-xl text-gray-600 mb-2 max-w-2xl mx-auto"
              >
                저시력자, 시각장애인을 위한 필수 웹 접근성 + 구글/네이버 검색 상위 노출
              </motion.p>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.25 }}
                className="text-base text-gray-500 mb-4 max-w-2xl mx-auto"
              >
                웹사이트 크롤링, 이미지 URL, 이미지 업로드, API 연동 등 다양한 방식으로 이미지 ALT 텍스트를 자동 생성하세요<br />
                <span className="text-primary font-medium">단순히 AI에 사진을 보여주는 것과 차원이 다릅니다. 업종·사이트 유형·브랜드 톤이 반영된 맞춤형 ALT를 웹사이트 전체에 한 번에 적용하세요.</span>
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="flex flex-col sm:flex-row gap-4 justify-center mb-6"
              >
                <Button
                  size="lg"
                  className="text-lg px-8 py-6"
                  onClick={() => navigate('/signup')}
                >
                  무료로 시작하기
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="text-lg px-8 py-6 border-green-500 text-green-700 hover:bg-green-50"
                  onClick={() => navigate('/diagnose')}
                >
                  <Search className="mr-2 h-5 w-5" />
                  내 사이트 무료 진단
                </Button>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ── Before/After 시각화 섹션 ── */}
        <motion.section
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="py-20 bg-white"
        >
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  실제 개선 효과
                </h2>
                <p className="text-lg text-gray-600">
                  AI가 생성한 ALT 텍스트로 접근성과 SEO가 동시에 개선됩니다
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Before */}
                <div className="bg-red-50 rounded-xl p-6 border-2 border-red-200">
                  <div className="flex items-center gap-2 mb-4">
                    <XCircle className="w-5 h-5 text-red-600" />
                    <span className="font-semibold text-red-700">개선 전</span>
                  </div>
                  <div className="bg-white rounded-lg p-4 mb-4">
                    <code className="text-sm text-gray-700">
                      &lt;img src="product.jpg" alt=""&gt;
                    </code>
                  </div>
                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <XCircle className="w-4 h-4 text-red-500" />
                      <span>ALT 텍스트 없음</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <XCircle className="w-4 h-4 text-red-500" />
                      <span>접근성 개선 필요</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <XCircle className="w-4 h-4 text-red-500" />
                      <span>SEO 개선 필요</span>
                    </div>
                  </div>
                </div>

                {/* After */}
                <div className="bg-green-50 rounded-xl p-6 border-2 border-green-200">
                  <div className="flex items-center gap-2 mb-4">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                    <span className="font-semibold text-green-700">개선 후</span>
                  </div>
                  <div className="bg-white rounded-lg p-4 mb-4">
                    <code className="text-sm text-gray-700">
                      &lt;img src="product.jpg" alt="검은색 스포츠카가 도로를 달리는 모습"&gt;
                    </code>
                  </div>
                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      <span>AI 생성 ALT 텍스트</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      <span>접근성 개선 완료</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      <span>SEO 개선 완료</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.section>

        {/* ── 무료 진단 프로모 섹션 ── */}
        <motion.section
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="py-16 bg-gradient-to-r from-green-50 via-emerald-50 to-teal-50 border-y border-green-100"
        >
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">

                {/* 왼쪽: 설명 */}
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                >
                  <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-1.5 rounded-full text-sm font-semibold mb-6">
                    <Shield className="w-4 h-4" />
                    회원가입 없이 무료 · 하루 3회
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 leading-tight">
                    내 사이트<br />
                    <span className="text-green-600">접근성 &amp; SEO</span> 지금 바로<br />
                    무료 진단
                  </h2>
                  <p className="text-lg text-gray-600 mb-6">
                    URL 하나만 입력하면 KWCAG 2.2 기준 <strong>24개 접근성 항목</strong>과
                    <strong> SEO 이미지 최적화</strong>를 AI가 자동 분석합니다.
                    로그인 없이, 즉시 결과를 확인하세요.
                  </p>

                  <div className="space-y-3 mb-8">
                    {[
                      { icon: ClipboardCheck, color: 'text-blue-600 bg-blue-50', text: 'KWCAG 2.2 접근성 24개 항목 자동 검사' },
                      { icon: TrendingUp,     color: 'text-green-600 bg-green-50', text: 'Google PSI 기반 SEO 이미지 최적화 분석' },
                      { icon: Zap,            color: 'text-purple-600 bg-purple-50', text: 'ALT 품질 평가 (0~100점)' },
                      { icon: Activity,       color: 'text-orange-600 bg-orange-50', text: '접근성·SEO 점수 + 이슈별 수정 가이드' },
                    ].map(({ icon: Icon, color, text }) => (
                      <div key={text} className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${color}`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <span className="text-gray-700 text-sm">{text}</span>
                      </div>
                    ))}
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      size="lg"
                      className="bg-green-600 hover:bg-green-700 text-white px-8 py-6 text-base gap-2"
                      onClick={() => navigate('/diagnose')}
                    >
                      <Search className="w-5 h-5" />
                      무료 진단 시작하기
                      <ArrowRight className="w-5 h-5" />
                    </Button>
                    <p className="text-xs text-gray-400 self-center">
                      신용카드 불필요 · 즉시 결과 확인
                    </p>
                  </div>
                </motion.div>

                {/* 오른쪽: 결과 미리보기 Mock-up */}
                <div className="bg-white rounded-2xl shadow-xl border border-green-100 overflow-hidden">
                  {/* 상단 URL 바 */}
                  <div className="bg-gray-50 border-b px-5 py-3 flex items-center gap-3">
                    <Globe className="w-4 h-4 text-gray-400 shrink-0" />
                    <span className="text-sm text-gray-500 truncate">https://www.yoursite.com</span>
                    <span className="ml-auto shrink-0 text-xs bg-green-100 text-green-700 font-medium px-2.5 py-1 rounded-full">진단 완료</span>
                  </div>

                  {/* 점수 */}
                  <div className="grid grid-cols-2 divide-x border-b">
                    {[
                      { label: '접근성 점수', score: 62, color: '#f97316' },
                      { label: 'SEO 점수',    score: 78, color: '#22c55e' },
                    ].map(({ label, score, color }) => (
                      <div key={label} className="py-5 text-center">
                        <p className="text-3xl font-bold" style={{ color }}>{score}</p>
                        <p className="text-xs text-gray-400 mt-1">{label}</p>
                      </div>
                    ))}
                  </div>

                  {/* 이슈 목록 */}
                  <div className="p-5 space-y-2.5">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">발견된 주요 이슈</p>
                    {[
                      { severity: 'error',   text: 'ALT 없는 이미지 12개 발견',           impact: '-10점' },
                      { severity: 'error',   text: '링크 내 빈 ALT 이미지 3개',           impact: '-10점' },
                      { severity: 'warning', text: 'H1 태그 없음 — 제목 구조 불량',       impact: '-3점'  },
                      { severity: 'warning', text: 'OG 이미지 미설정 — SNS 공유 불이익', impact: '-3점'  },
                      { severity: 'info',    text: 'target="_blank" 경고 미표시 링크',   impact: '-1점'  },
                    ].map(({ severity, text, impact }) => (
                      <div key={text} className="flex items-center gap-2.5 py-2 border-b border-gray-50 last:border-0">
                        <span className={`shrink-0 text-xs font-medium px-2 py-0.5 rounded-full ${
                          severity === 'error'   ? 'bg-red-100 text-red-700' :
                          severity === 'warning' ? 'bg-yellow-100 text-yellow-700' :
                                                   'bg-blue-100 text-blue-700'
                        }`}>
                          {severity === 'error' ? '오류' : severity === 'warning' ? '경고' : '정보'}
                        </span>
                        <span className="text-xs text-gray-700 flex-1">{text}</span>
                        <span className="text-xs text-red-500 font-medium shrink-0">{impact}</span>
                      </div>
                    ))}
                  </div>

                  {/* ALT 자동생성 CTA */}
                  <div className="bg-primary/5 border-t px-5 py-3 flex items-center justify-between">
                    <span className="text-sm text-primary font-medium">ALT 자동생성 가능 이미지 12개</span>
                    <span className="text-xs text-gray-400 flex items-center gap-1">
                      <Zap className="w-3 h-3" /> AI로 즉시 수정
                    </span>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </motion.section>

        {/* Features Section */}
        <motion.section
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          id="features"
          className="py-20 bg-white"
        >
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
              <Card className="border-2 hover:border-primary transition-colors">
                <CardHeader>
                  <div className="bg-primary/10 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                    <Globe className="h-6 w-6 text-primary" />
                  </div>
                  <CardTitle>웹사이트 크롤링</CardTitle>
                  <CardDescription>
                    URL만 입력하면 모든 이미지를 AI로 스캔하여 기존 ALT과 AI 생성 ALT를 함께 비교·제공합니다. 기존 ALT가 있는 이미지도 AI가 재분석합니다.
                  </CardDescription>
                </CardHeader>
              </Card>

              <Card className="border-2 hover:border-primary transition-colors">
                <CardHeader>
                  <div className="bg-primary/10 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                    <LinkIcon className="h-6 w-6 text-primary" />
                  </div>
                  <CardTitle>이미지 URL 입력</CardTitle>
                  <CardDescription>
                    이미지 URL을 직접 입력하여, 개별 이미지에 대한 ALT 텍스트를 빠르게 생성합니다.
                  </CardDescription>
                </CardHeader>
              </Card>

              <Card className="border-2 hover:border-primary transition-colors">
                <CardHeader>
                  <div className="bg-primary/10 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                    <Upload className="h-6 w-6 text-primary" />
                  </div>
                  <CardTitle>이미지 업로드</CardTitle>
                  <CardDescription>
                    로컬 이미지 파일을 드래그 앤 드롭하거나 업로드하여 ALT 텍스트를 생성합니다.
                  </CardDescription>
                </CardHeader>
              </Card>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-3 mb-2">
                    <Zap className="h-8 w-8 text-primary" />
                    <CardTitle>빠른 생성</CardTitle>
                  </div>
                  <CardDescription>
                    AI 기술을 이용한 초고속 ALT 텍스트 생성으로 시간과 비용을 절감합니다.
                  </CardDescription>
                </CardHeader>
              </Card>

              <Card>
                <CardHeader>
                  <div className="flex items-center gap-3 mb-2">
                    <Lock className="h-8 w-8 text-primary" />
                    <CardTitle>안전한 관리</CardTitle>
                  </div>
                  <CardDescription>
                    엔터프라이즈급 보안으로 데이터를 보호하고 안전하게 관리합니다.
                  </CardDescription>
                </CardHeader>
              </Card>

              <Card>
                <CardHeader>
                  <div className="flex items-center gap-3 mb-2">
                    <Code2 className="h-8 w-8 text-primary" />
                    <CardTitle>REST API</CardTitle>
                  </div>
                  <CardDescription>
                    쇼핑몰 상품 등록, CMS 발행, 배치 작업 파이프라인에 ALT 생성을 직접 통합하세요. Node.js·Java·Python 샘플 코드와 함께 5분 만에 연동할 수 있습니다.
                  </CardDescription>
                </CardHeader>
              </Card>
            </div>
          </div>
        </motion.section>

        {/* ── 일반 AI 비교 섹션 ── */}
        <section className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <div className="inline-flex items-center gap-2 bg-orange-100 px-4 py-2 rounded-full mb-4">
                <span className="text-orange-600 text-sm font-medium">💡 솔직한 비교</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                &ldquo;ChatGPT에 물어보면 되지 않나요?&rdquo;
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                물론 가능합니다. 하지만 <strong>실무에서 쓰려면</strong> 이런 문제가 생깁니다.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto mb-12">
              {/* AI 단독 */}
              <div className="bg-white border-2 border-gray-200 rounded-2xl p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center text-xl">🤖</div>
                  <h3 className="text-xl font-bold text-gray-600">일반 AI 단독 사용</h3>
                </div>
                <ul className="space-y-4">
                  {[
                    '이미지 1장씩 직접 복사·붙여넣기',
                    '매번 업종·톤·길이 프롬프트를 다시 작성',
                    '100개 이미지 = 100번 반복 작업',
                    '결과를 따로 저장·정리해야 함',
                    '개발 시스템·CMS 자동화 연동 불가',
                    '사이트 전체 ALT 누락 현황 파악 불가',
                    '영문 페이지 대응 시 언어 설정 매번 필요',
                  ].map((text, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <X className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-500">{text}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* ALT Generation */}
              <div className="bg-gradient-to-br from-primary/5 to-blue-50 border-2 border-primary rounded-2xl p-8 relative overflow-hidden">
                <div className="absolute top-4 right-4">
                  <span className="bg-primary text-white text-xs px-3 py-1 rounded-full font-medium">추천</span>
                </div>
                <div className="flex items-center gap-3 mb-6">
                  <img src="/favicon-32x32.png" alt="ALT Generation Logo" className="w-10 h-10" />
                  <h3 className="text-xl font-bold text-primary">ALT Generation</h3>
                </div>
                <ul className="space-y-4">
                  {[
                    'URL 하나로 사이트 전체 이미지 일괄 처리',
                    '컨텍스트 프로필 1회 설정 → 모든 요청에 자동 반영',
                    'ALT 누락 이미지만 골라 배치 생성',
                    '이력 저장 + HTML alt 태그 원클릭 복사',
                    'REST API로 CMS·쇼핑몰에 직접 자동화',
                    '사이트 전체 ALT 현황 대시보드 제공',
                    '영문(eng) 출력 옵션으로 다국어 즉시 대응',
                  ].map((text, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 font-medium">{text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="max-w-5xl mx-auto bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
              <p className="text-amber-800 font-medium text-lg">
                💬 핵심 차이: 일반 AI는 <strong>대화 도구</strong>이고,
                ALT Generation은 <strong>ALT 전용 업무 자동화 플랫폼</strong>입니다.
              </p>
            </div>
          </div>
        </section>

        {/* ── 컨텍스트 프로필 스포트라이트 ── */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                <div>
                  <div className="inline-flex items-center gap-2 bg-purple-100 px-4 py-2 rounded-full mb-6">
                    <Target className="h-4 w-4 text-purple-600" />
                    <span className="text-purple-700 text-sm font-medium">핵심 차별화 기능</span>
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                    &ldquo;우리 사이트에 맞는&rdquo;<br />
                    <span className="text-purple-600">컨텍스트 프로필</span>
                  </h2>
                  <p className="text-lg text-gray-600 mb-8">
                    업종·사이트 유형·출력 톤을 <strong>한 번만 설정</strong>하면,
                    이후 모든 ALT 생성에 자동으로 반영됩니다. 같은 이미지라도
                    <strong> 쇼핑몰</strong>과 <strong>의료 사이트</strong>에서 전혀 다른 ALT가 나와야 합니다.
                  </p>
                  <div className="space-y-4">
                    {[
                      { icon: '🏪', title: '업종 유형 설정', desc: '패션·전자·의료·금융 등 업종별 전문 용어 자동 반영' },
                      { icon: '🎯', title: '서비스 대상 설정', desc: '일반 소비자 / 전문가 / 시니어 등 독자 수준에 맞는 표현' },
                      { icon: '📏', title: '길이·문체 제어', desc: '짧고 간결 / 상세 설명 / 존댓말 등 브랜드 톤 일관성 유지' },
                      { icon: '🚫', title: '금지어·필수어 설정', desc: '특정 단어 제외 또는 항상 포함되도록 강제 적용' },
                    ].map((item, i) => (
                      <div key={i} className="flex items-start gap-4 p-4 bg-purple-50 rounded-xl">
                        <span className="text-2xl">{item.icon}</span>
                        <div>
                          <p className="font-semibold text-gray-800">{item.title}</p>
                          <p className="text-sm text-gray-600">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* 프로필 카드 Mock-up */}
                <div className="bg-gradient-to-br from-purple-600 to-indigo-700 rounded-2xl p-8 text-white shadow-2xl">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                      <Settings className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="font-bold">컨텍스트 프로필 예시</p>
                      <p className="text-purple-200 text-sm">패션 쇼핑몰 설정</p>
                    </div>
                  </div>
                  <div className="space-y-3 mb-6">
                    {[
                      { label: '업종', value: '패션/의류' },
                      { label: '사이트 유형', value: '쇼핑몰' },
                      { label: '서비스 대상', value: '20~40대 여성' },
                      { label: '출력 톤', value: '친근하고 감성적인' },
                      { label: '길이 제한', value: '50자 이내' },
                      { label: '종결 어미', value: '명사형 (~있습니다)' },
                      { label: '필수 포함어', value: '신상품, 세일' },
                      { label: '금지어', value: '저렴한, 싸구려' },
                    ].map((row, i) => (
                      <div key={i} className="flex justify-between items-center py-2 border-b border-white/10">
                        <span className="text-purple-200 text-sm">{row.label}</span>
                        <span className="text-white text-sm font-medium">{row.value}</span>
                      </div>
                    ))}
                  </div>
                  <div className="bg-white/10 rounded-xl p-4">
                    <p className="text-xs text-purple-200 mb-2">🤖 실제 생성 예시</p>
                    <p className="text-sm font-medium leading-relaxed">"감각적인 플로럴 패턴의 봄 신상품 원피스, 우아하고 세련된 디자인"</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                    투자 대비 효과가<br /><span className="text-primary">명확합니다</span>
                  </h2>
                  <div className="space-y-6">
                    <div className="flex gap-4">
                      <div className="flex-shrink-0">
                        <div className="bg-green-100 p-3 rounded-lg">
                          <Shield className="h-6 w-6 text-green-600" />
                        </div>
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold mb-2">웹 접근성 법적 요건 충족</h3>
                        <p className="text-gray-600">
                          국내 장애인차별금지법·전자정부법, 해외 WCAG 2.1 기준을 충족합니다.
                          스크린 리더 사용자가 이미지를 정확히 이해할 수 있도록 돕습니다.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4">
                      <div className="flex-shrink-0">
                        <div className="bg-blue-100 p-3 rounded-lg">
                          <TrendingUp className="h-6 w-6 text-blue-600" />
                        </div>
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold mb-2">SEO 이미지 검색 노출 증가</h3>
                        <p className="text-gray-600">
                          Google 이미지 검색은 ALT 텍스트를 핵심 신호로 활용합니다.
                          업종 키워드가 반영된 ALT로 이미지 검색 유입을 늘리세요.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4">
                      <div className="flex-shrink-0">
                        <div className="bg-purple-100 p-3 rounded-lg">
                          <Sparkles className="h-6 w-6 text-purple-600" />
                        </div>
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold mb-2">반복 작업 90% 자동화</h3>
                        <p className="text-gray-600">
                          담당자가 이미지 1장에 평균 2~3분 쏟는 ALT 작업을 초 단위로 줄입니다.
                          월 2,000장이면 <strong>약 100시간</strong>의 인건비를 절감합니다.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4">
                      <div className="flex-shrink-0">
                        <div className="bg-orange-100 p-3 rounded-lg">
                          <Target className="h-6 w-6 text-orange-600" />
                        </div>
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold mb-2">브랜드 일관성 유지</h3>
                        <p className="text-gray-600">
                          컨텍스트 프로필로 항상 동일한 톤·용어·길이로 생성됩니다.
                          담당자가 바뀌어도 ALT 품질이 흔들리지 않습니다.
                        </p>
                      </div>
                    </div>
                  </div>
              </div>

              <div className="bg-gradient-to-br from-primary to-blue-700 p-8 rounded-2xl text-white">
                <div className="space-y-6">
                  <div className="bg-white/10 p-6 rounded-xl backdrop-blur-sm">
                    <div className="flex items-start justify-between mb-1">
                      <div className="text-4xl font-bold">10,000+</div>
                      <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full text-blue-100 mt-1">목표</span>
                    </div>
                    <div className="text-blue-100">생성된 ALT 텍스트</div>
                  </div>
                  <div className="bg-white/10 p-6 rounded-xl backdrop-blur-sm">
                    <div className="flex items-start justify-between mb-1">
                      <div className="text-4xl font-bold">500+</div>
                      <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full text-blue-100 mt-1">목표</span>
                    </div>
                    <div className="text-blue-100">활성 사용자</div>
                  </div>
                  <div className="bg-white/10 p-6 rounded-xl backdrop-blur-sm">
                    <div className="text-4xl font-bold mb-2">99.9%</div>
                    <div className="text-blue-100">서비스 가용성</div>
                  </div>
                </div>
                <p className="text-xs text-blue-200/60 mt-4 text-right">* 목표 수치는 2026년 말 기준입니다.</p>
              </div>
            </div>
          </div>
        </section>

        {/* ── 사용자 유형별 활용 사례 ── */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                이런 분들께 꼭 필요합니다
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                업종과 역할에 따라 ALT Generation이 해결해주는 문제가 다릅니다.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              <Card className="border-2 hover:border-primary hover:shadow-lg transition-all">
                <CardHeader>
                  <div className="bg-orange-100 w-14 h-14 rounded-xl flex items-center justify-center mb-4 text-3xl">
                    🛍️
                  </div>
                  <CardTitle className="text-xl">쇼핑몰·이커머스</CardTitle>
                  <CardDescription className="text-base leading-relaxed">
                    수천 개 상품 이미지에 일일이 ALT를 쓰고 있나요? 크롤링 한 번으로 전체 상품 이미지 ALT를 자동 생성, SEO 순위를 높이세요.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />상품명·소재 기반 정확한 ALT</li>
                    <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />신규 상품 등록 시 API 자동화</li>
                    <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />누락 ALT 대시보드로 즉시 확인</li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="border-2 hover:border-primary hover:shadow-lg transition-all">
                <CardHeader>
                  <div className="bg-blue-100 w-14 h-14 rounded-xl flex items-center justify-center mb-4 text-3xl">
                    📰
                  </div>
                  <CardTitle className="text-xl">뉴스·미디어·블로그</CardTitle>
                  <CardDescription className="text-base leading-relaxed">
                    매일 수십 건의 기사와 이미지를 발행합니다. 기자·편집자가 ALT에 시간을 쏟는 대신, 자동화로 접근성을 지키세요.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />기사 맥락에 맞는 간결한 ALT</li>
                    <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />CMS API 연동으로 발행 즉시 적용</li>
                    <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />웹 접근성 법적 요건 충족</li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="border-2 hover:border-primary hover:shadow-lg transition-all">
                <CardHeader>
                  <div className="bg-green-100 w-14 h-14 rounded-xl flex items-center justify-center mb-4 text-3xl">
                    🏢
                  </div>
                  <CardTitle className="text-xl">기업 웹팀·에이전시</CardTitle>
                  <CardDescription className="text-base leading-relaxed">
                    여러 클라이언트 사이트의 ALT를 관리합니다. 사이트마다 다른 프로필을 설정하고, 팀원들이 일관된 품질로 작업하세요.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />클라이언트별 브랜드 톤 프로필 저장</li>
                    <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />이미지 업로드 배치 처리</li>
                    <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />관리자 대시보드·사용 현황 리포트</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section id="pricing" className="py-20 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                합리적인 요금제
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                필요에 맞는 플랜을 선택하세요. 언제든지 업그레이드하거나 다운그레이드할 수 있습니다.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
              {/* Free Plan */}
              <Card className="border-2">
                <CardHeader>
                  <CardTitle className="text-2xl">무료</CardTitle>
                  <div className="mt-4">
                    <span className="text-4xl font-bold">₩0</span>
                    <span className="text-gray-600">/월</span>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-sm">월 60개 이미지</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-sm">기본 기능</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-sm">API 접근</span>
                    </li>
                  </ul>
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => navigate('/signup')}
                  >
                    시작하기
                  </Button>
                </CardContent>
              </Card>

              {/* Starter Plan */}
              <Card className="border-2">
                <CardHeader>
                  <CardTitle className="text-2xl">스타트</CardTitle>
                  <div className="mt-4">
                    <span className="text-4xl font-bold">₩15,000</span>
                    <span className="text-gray-600">/월</span>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-sm">월 500개 이미지</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-sm">모든 기능</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-sm">서비스 지원</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-sm">API 접근</span>
                    </li>
                  </ul>
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => navigate('/signup')}
                  >
                    시작하기
                  </Button>
                </CardContent>
              </Card>

              {/* Pro Plan */}
              <Card className="border-2 border-primary shadow-lg relative">
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className="bg-primary text-white px-4 py-1 rounded-full text-sm font-medium">
                    인기
                  </span>
                </div>
                <CardHeader>
                  <CardTitle className="text-2xl">프로</CardTitle>
                  <div className="mt-4">
                    <span className="text-4xl font-bold">₩29,000</span>
                    <span className="text-gray-600">/월</span>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-sm">월 2,000개 이미지</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-sm">모든 기능</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-sm">우선 지원</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-sm">API 접근</span>
                    </li>
                  </ul>
                  <Button 
                    className="w-full"
                    onClick={() => navigate('/signup')}
                  >
                    시작하기
                  </Button>
                </CardContent>
              </Card>

              {/* Enterprise Plan */}
              <Card className="border-2">
                <CardHeader>
                  <CardTitle className="text-2xl">엔터프라이즈</CardTitle>
                  <div className="mt-4">
                    <span className="text-4xl font-bold">문의</span>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-sm">무제한 이미지</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-sm">맞춤 기능</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-sm">전담 매니저</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-sm">SLA 보장</span>
                    </li>
                  </ul>
                  <Button 
                    variant="outline" 
                    className="w-full"
                  >
                    문의하기
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* About Section */}
        <section id="about" className="py-20 bg-gray-50">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                ALT Generation 소개
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                ALT Generation은 웹 접근성과 SEO 최적화를 위한 혁신적인 AI 솔루션을 제공합니다. 
                우리의 목표는 모든 웹사이트가 더 접근성 높고, 더 검색 친화적이 되도록 돕는 것입니다.
              </p>
              <p className="text-lg text-gray-600 mb-8">
                2016년에 설립된 저희 회사는 최첨단 AI 기술을 활용하여 이미지 ALT 텍스트 생성을 자동화하고, 
                웹 개발자와 콘텐츠 제작자들이 더 나은 디지털 경험을 만들 수 있도록 지원합니다.
              </p>
              <div className="bg-white p-8 rounded-2xl shadow-sm">
                <h3 className="text-xl font-semibold mb-4">회사 정보</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
                  <div>
                    <p className="text-sm text-gray-600">상호명</p>
                    <p className="font-semibold">(주)지호아이앤씨</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">대표이사</p>
                    <p className="font-semibold">최정윤</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">사업자등록번호</p>
                    <p className="font-semibold">849-81-00345</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">설립일</p>
                    <p className="font-semibold">2016년 4월</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-primary to-blue-700 text-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              지금 바로 시작하세요
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              무료로 시작하고 AI 기반 ALT 텍스트 생성의 강력함을 경험해보세요.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                variant="secondary"
                className="text-lg px-8 py-6"
                onClick={() => navigate('/signup')}
              >
                무료로 시작하기
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="text-lg px-8 py-6 bg-white/10 hover:bg-white/20 text-white border-white/30"
                onClick={() => window.location.href = 'mailto:ibjun77@gmail.com'}
              >
                문의하기
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
    </>
  );
}
